<template>
  <RevDrawer
    :name="ADDRESS_RULE_FORM_MODAL_NAME"
    :title="i18n(translations.modalTitle)"
    @close="handleClose"
    @open="fetchNextStep"
  >
    <template #body>
      <RevStepper
        :active-step
        alternative-text-back="back"
        alternative-text-close="close"
        alternative-text-completed="completed"
        :alternative-text-current="activeStep"
        class="mt-3"
        :has-back="true"
        :steps="STEPS"
        @back="goToPreviousStep"
      />

      <RevLoadingScreen
        v-if="isFetching"
        class="mt-64"
        :text="i18n(translations.fetching)"
      />

      <div v-else>
        <AddressSelection
          v-if="isActiveStep(STEPS[0].name)"
          :addresses="metadata.addresses"
          :defaultValue="ruleToEdit?.address?.id"
          @next-step="getNextStep"
        />

        <ServicesSelection
          v-if="isActiveStep(STEPS[1].name)"
          :defaultValue="ruleToEdit?.service?.code"
          :services="metadata.services"
          @next-step="getNextStep"
        />

        <MarketsSelection
          v-if="isActiveStep(STEPS[2].name)"
          :defaultValue="ruleToEdit?.markets"
          :isBuyback="hasSelectedBuyback"
          :markets="metadata.markets"
          @next-step="getNextStep"
        />

        <CategoriesSelection
          v-if="isActiveStep(STEPS[3].name)"
          :categories="metadata.categories"
          :defaultValue="ruleToEdit?.categories?.map((category) => category.id)"
          @next-step="getNextStep"
        />

        <Confirmation
          v-if="isActiveStep(STEPS[4].name)"
          :metadata
          :payload
          :rule-to-edit
          @rule-submitted="onRuleSubmitted"
        />
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { type Ref, computed, ref } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevDrawer } from '@ds/components/Drawer'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { closeModal } from '@ds/components/ModalBase'
import { RevStepper } from '@ds/components/Stepper'

import {
  ADDRESS_RULE_FORM_MODAL_NAME,
  type AddressRulePayload,
} from '../../AddressRules.constants'

import translations from './AddressRulesModalForm.translations'
import AddressSelection from './components/AddressSelection/AddressSelection.vue'
import CategoriesSelection from './components/CategoriesSelection/CategoriesSelection.vue'
import Confirmation from './components/Confirmation/Confirmation.vue'
import MarketsSelection from './components/MarketsSelection/MarketsSelection.vue'
import ServicesSelection from './components/ServicesSelection/ServicesSelection.vue'

const { ruleToEdit } = defineProps<{
  ruleToEdit: merchantsAPI.AddressRule | Record<string, never>
}>()

const emit = defineEmits(['rule-submitted'])
const i18n = useI18n()
const { openErrorToast } = useTheToast()
const payload: Ref<AddressRulePayload> = ref({})
const metadata: Ref<merchantsAPI.AddressRuleMetada> = ref({})
const activeStepIndex = ref(0)
const isFetching = ref(false)

const STEPS = [
  { name: 'address', label: i18n(translations.addressStep) },
  { name: 'services', label: i18n(translations.servicesStep) },
  { name: 'markets', label: i18n(translations.marketsStep) },
  { name: 'categories', label: i18n(translations.categoriesStep) },
  { name: 'confirmation', label: i18n(translations.confirmationStep) },
]

const activeStep = computed(() => {
  return STEPS[activeStepIndex.value].name
})

const hasSelectedBuyback = computed(() => payload.value.service === 'BUYBACK')

function handleClose() {
  activeStepIndex.value = 0
  payload.value = {}
  metadata.value = {}
}

async function fetchNextStep(stepPayload = {}) {
  isFetching.value = true

  try {
    payload.value = {
      ...payload.value,
      ...stepPayload,
    }

    const addressRuleResponse = await $httpFetch(merchantsAPI.postAddressRule, {
      body: payload.value,
    })

    metadata.value = { ...metadata.value, ...addressRuleResponse.metadata }
  } catch {
    openErrorToast()
  }

  isFetching.value = false
}

function goToNextStep() {
  if (activeStepIndex.value === STEPS.length - 1) {
    return
  }

  activeStepIndex.value += 1
}

async function getNextStep(stepPayload: AddressRulePayload) {
  await fetchNextStep(stepPayload)
  goToNextStep()
}

function goToPreviousStep() {
  if (activeStepIndex.value === 0) {
    return
  }

  activeStepIndex.value -= 1
}

function isActiveStep(stepName: string) {
  return activeStep.value === stepName
}

function onRuleSubmitted() {
  emit('rule-submitted')
  closeModal(ADDRESS_RULE_FORM_MODAL_NAME)
}
</script>
